//------------------------------------------------------------------------------
// name: useProxyValue.ts
// author: mudas
// created: 2025/12/08
//------------------------------------------------------------------------------

import { useControllableValue } from 'ahooks';
import { useComputed } from 'common/hooks';

import { Identity } from 'common/types';
import { SimpleValueType } from './types';

export type ProxyValue<OptionType extends Identity> = OptionType | OptionType[] | SimpleValueType | undefined;

export const useProxyValue = <OptionType extends Identity = Identity>(props: {
  // 做为标识唯一值的字段名，默认为 id
  fieldName?: string;

  // 是否以 OptionType 替代 SimpleValueType 作为外部输入输出
  replaceAsOption?: boolean;

  // 受控/非受控 value
  value?: ProxyValue<OptionType>;
  defaultValue?: ProxyValue<OptionType>;

  // 外部 onChange 回调
  onChange?: (value: ProxyValue<OptionType>) => void;
}) => {
  const { fieldName = 'id', replaceAsOption = false } = props;

  const [rawValue, setRawValue] = useControllableValue<ProxyValue<OptionType>>(props, {
    valuePropName: 'value',
    trigger: 'onChange',
  });

  // 将外部 value 转换为 Select 可用的 SimpleValueType
  const selectValue = useComputed<SimpleValueType | undefined>(() => {
    if (rawValue === undefined || rawValue === null) return undefined;

    if (Array.isArray(rawValue)) {
      if (rawValue.length === 0) return undefined;
      if (typeof rawValue[0] === 'string' || typeof rawValue[0] === 'number') {
        return rawValue as SimpleValueType;
      }
      return (rawValue as OptionType[]).map((item) => item[fieldName]);
    }

    if (typeof rawValue === 'string' || typeof rawValue === 'number') {
      return rawValue as SimpleValueType;
    }

    return (rawValue as OptionType)[fieldName] as SimpleValueType;
  }, [rawValue, fieldName]);

  const handleSelectChange = (value: SimpleValueType, option?: OptionType | OptionType[]) => {
    setRawValue(replaceAsOption ? option : value);
  };

  return { selectValue, onChange: handleSelectChange } as const;
};
